import { memo, useEffect, useLayoutEffect, useRef, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";
import type { AppState } from "@Store/index";
import CustomLink from "@Components/CustomLink";
import Image from "@Components/Image";
import Carousel from "@Components/Carousel";
import CarouselIndicator from "@Components/Carousel/components/CarouselIndicator";
import Skeleton from "@Components/Skeleton";
import wrapperHOC from "@Utils/wrapperHOC";
import YellowStar from "@Icons/YellowStar";
import VectorSearchBelowRating from "@Icons/VectorSearchBelowRating";
import getImagePath from "@Utils/getImagePath";
import config from "@Config/index";
import {
  getAvailableStartingPricing,
  getPricingDuration,
  getLink,
} from "@Pages/Search/components/SearchListItem/helper";
import { fetchBlogInventories } from "./data/fetchBlogInventories";
import styles from "./contentInventoryCarousel.module.scss";
import ArrowIcon from "@Icons/ArrowIcon";
import useTranslationHook from "hooks/useTranslationHook";

export interface InventoryImage {
  base_path: string;
  featured?: boolean | string;
  type?: string;
  caption?: string;
}

export interface InventoryLocation {
  locality?: { long_name?: string };
  state?: { long_name?: string };
  primary?: string;
  secondary?: string;
}

export interface InventoryPricing {
  currency?: string;
  duration?: string;
  min_available_price?: number;
  max_available_price?: number;
  min_price?: number;
  max_price?: number;
}

export interface InventoryItem {
  id: number | string;
  name: string;
  canonical_name?: string;
  images?: InventoryImage[];
  location?: InventoryLocation;
  pricing?: InventoryPricing;
  reviews_rating?: string | number | null;
  reviews_count?: number | null;
  tags?: string[];
  isStudentChoice?: boolean; // drives the Student's Choice badge
}

interface ContentInventoryCarouselProps {
  title?: string;
  viewMoreUrl?: string;
}

interface InventoryCarouselCardProps {
  item: InventoryItem;
}

const InventoryCarouselCard = ({ item }: InventoryCarouselCardProps) => {
  const [activePos, setActivePos] = useState(1);
  const { t } = useTranslationHook();
  const price = getAvailableStartingPricing(item.pricing);
  const duration = getPricingDuration(item.pricing?.duration);
  const link = getLink(item) ?? `/places/${item.canonical_name}`;
  const locationText = item.location?.locality?.long_name ?? item.location?.primary ?? "";
  const rating = item.reviews_rating ? parseFloat(String(item.reviews_rating)) : null;
  const showRating = rating !== null && rating >= 4.0;
  const allImagePaths = (item.images ?? []).map((img) => img.base_path);
  const imagePaths = allImagePaths.slice(0, 5);

  const slideList = imagePaths.map((path) => (
    <Image
      key={path}
      src={getImagePath(path, "gallery-image-small")}
      width="100%"
      height="100%"
      alt={item.name}
      className={styles.carouselImage}
      isNotLazy
    />
  ));

  return (
    <CustomLink href={link} className={styles.card}>
      {/* Image carousel */}
      <div className={styles.cardImageWrapper}>
        <Carousel
          slideList={slideList}
          activePos={activePos}
          handleChangeSlide={setActivePos}
          className={styles.cardImageCarousel}
          showNavOnHover
          noLazyLoad
        />

        {/* Carousel dot indicators */}
        <div className={styles.carouselIndicatorWrap}>
          <CarouselIndicator
            activeDot={activePos}
            totalSlider={imagePaths.length}
            handleClickIndicator={setActivePos}
            type="line"
            activeDotColor="#fff"
            maxDots={5}
          />
        </div>

        {/* Student's Choice badge */}
        {item.isStudentChoice && (
          <div className={styles.studentChoiceBadge}>{t("blog.carousel.studentschoice")}</div>
        )}

        {/* Rating wave + star overlay */}
        {showRating && (
          <div className={styles.ratingWrapper}>
            <div className={styles.ratingBackground}>
              <VectorSearchBelowRating />
            </div>
            <div className={styles.ratingOverlay}>
              <YellowStar />
              <span className={styles.ratingValue}>{rating?.toFixed(1)}</span>
              {item.reviews_count != null && (
                <span className={styles.ratingCount}>({item.reviews_count})</span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Card text + gradient CTA */}
      <div className={styles.cardBody}>
        <div className={styles.cardTextGroup}>
          <p className={styles.cardName}>{item.name}</p>
          {locationText && <p className={styles.cardLocation}>{locationText}</p>}
        </div>
        {price && (
          <div className={styles.priceCTA}>
            <span className={styles.priceFrom}>{t("blog.carousel.from")} </span>
            <span className={styles.priceValue}>{price}/</span>
            {duration && <span className={styles.priceDuration}>{t(duration)}</span>}
          </div>
        )}
      </div>
    </CustomLink>
  );
};

const InventoryCarouselSkeleton = () => (
  <div className={styles.skeletonContainer}>
    <div className={styles.skeletonHeader}>
      <Skeleton width="60%" height="14px" addBorderRadius />
      <Skeleton width="25%" height="14px" addBorderRadius />
    </div>
    <Skeleton width="100%" height="20px" mb="16px" addBorderRadius />
    <Skeleton width="100%" height="200px" addBorderRadius />
    <div className={styles.skeletonBody}>
      <Skeleton width="70%" height="14px" addBorderRadius />
      <Skeleton width="45%" height="12px" addBorderRadius />
      <Skeleton width="55%" height="14px" addBorderRadius />
    </div>
  </div>
);

const ACTIVE_SCALE = 1;
const PEEK_SCALE = 0.9;
const CARD_WIDTH_PCT = 65.6;
const CARD_GAP_PCT = 72;
const ANIMATION_MS = 350;

const ContentInventoryCarousel = ({ title, viewMoreUrl }: ContentInventoryCarouselProps) => {
  const { t } = useTranslationHook();
  const defaultTitle = `${t("blog.carousel.title.line1")}\n${t("blog.carousel.title.line2")}`;
  const displayTitle = title || defaultTitle;
  const [isMounted, setIsMounted] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isHovered, setIsHovered] = useState(false);
  const [inventories, setInventories] = useState<InventoryItem[]>([]);

  const { userCurrency, cityCanonical, countryCanonical, globalInventories } = useSelector(
    ({ appData, blogPageData, blogHomeData }: AppState) => ({
      userCurrency: appData.userCurrency,
      // city/country relations exist only on single blog posts. Their canonicalName is the
      // amber region canonical (e.g. "bath-1811025358760"). Sentinel values "Multi-city" and
      // "Global" have canonicalName === null, so they self-filter to undefined here.
      cityCanonical: (blogPageData.data as any)?.city?.canonicalName ?? null,
      countryCanonical: (blogPageData.data as any)?.country?.canonicalName ?? null,
      globalInventories: blogHomeData?.globalInventories ?? [],
    }),
    shallowEqual,
  );

  // View more links to the most specific level we have data for: city → country → home.
  let derivedViewMoreUrl = "/";
  if (cityCanonical) {
    derivedViewMoreUrl = `/places/search/${cityCanonical}`;
  } else if (countryCanonical) {
    derivedViewMoreUrl = `/places/search/${countryCanonical}`;
  }

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const pendingRecentreRef = useRef(false);

  const total = inventories.length > 0 ? inventories.length : 1;
  const [virtualIndex, setVirtualIndex] = useState(total);
  const hasInitializedIndexRef = useRef(false);

  const imgSphere = config.IS_CHINA_APP
    ? `${config.CHINA_STATIC_ASSETS_URL}/620e4101b2ce125835bff0bb/imgSphereBlog.svg?q=1`
    : `${config.BLOG_ASSET_URL}/620e4101b2ce125835bff0bb/imgSphereBlog.svg?q=1`;

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (!isMounted) return;

    // No city/country context — use SSR globals or fetch globally.
    if (!cityCanonical && !countryCanonical) {
      if (globalInventories.length > 0) {
        setInventories(globalInventories);
        setIsLoading(false);
      } else {
        fetchBlogInventories({ userCurrency: userCurrency || undefined })
          .then((result) => {
            if (result.length > 0) setInventories(result);
            setIsLoading(false);
          })
          .catch(() => setIsLoading(false));
      }
      return;
    }

    // City and/or country context — fill-down (city → country → global).
    setIsLoading(true);
    fetchBlogInventories({
      cityCanonical: cityCanonical ?? undefined,
      countryCanonical: countryCanonical ?? undefined,
      userCurrency: userCurrency || undefined,
      ssrGlobalInventories: globalInventories.length > 0 ? globalInventories : undefined,
    })
      .then((result) => {
        if (result.length > 0) setInventories(result);
        setIsLoading(false);
      })
      .catch(() => {
        if (globalInventories.length > 0) setInventories(globalInventories);
        setIsLoading(false);
      });
  }, [isMounted, cityCanonical, countryCanonical, userCurrency, globalInventories]);

  // Once real inventories load, `total` changes from the 1-item fallback to the real
  // count — re-sync virtualIndex so the first render lands on card 0, not card 1.
  useEffect(() => {
    if (inventories.length > 0 && !hasInitializedIndexRef.current) {
      hasInitializedIndexRef.current = true;
      setVirtualIndex(total);
    }
  }, [inventories.length, total]);

  useEffect(() => {
    if (!isMounted || isHovered || inventories.length <= 1) {
      return undefined;
    }
    timerRef.current = setInterval(() => {
      setVirtualIndex((prev) => prev + 1);
    }, 3000);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isMounted, isHovered, inventories.length]);

  useEffect(() => {
    if (!isMounted) return undefined;
    const inCopy2 = virtualIndex >= total && virtualIndex < 2 * total;
    if (inCopy2) return undefined;

    const recentreTimer = setTimeout(() => {
      const realIndex = ((virtualIndex % total) + total) % total;
      pendingRecentreRef.current = true;
      if (trackRef.current) {
        trackRef.current.classList.add(styles.noTransition);
      }
      setVirtualIndex(total + realIndex);
    }, ANIMATION_MS);

    return () => clearTimeout(recentreTimer);
  }, [virtualIndex, isMounted, total]);

  useLayoutEffect(() => {
    if (pendingRecentreRef.current && trackRef.current) {
      const forceReflow = trackRef.current.offsetHeight;
      if (forceReflow >= 0) {
        trackRef.current.classList.remove(styles.noTransition);
      }
      pendingRecentreRef.current = false;
    }
  });

  // Show skeleton until mount + first load resolves.
  if (!isMounted || isLoading) {
    return <InventoryCarouselSkeleton />;
  }

  if (inventories.length === 0) {
    return null;
  }

  const goTo = (target: number) => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setVirtualIndex(target);
  };

  const extendedItems: Array<InventoryItem & { _virtualKey: string }> = [];
  for (let c = 0; c < 3; c++) {
    inventories.forEach((item) => {
      extendedItems.push({ ...item, _virtualKey: `${c}-${item.id}` });
    });
  }

  const trackOffset = -(virtualIndex * CARD_GAP_PCT) + (50 - CARD_WIDTH_PCT / 2);
  const realActive = ((virtualIndex % total) + total) % total;

  return (
    <div
      className={styles.container}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className={styles.topBar} aria-hidden="true" />

      <div className={styles.sphereTopLeft} aria-hidden="true">
        <Image src={imgSphere} alt="" width="100%" height="100%" />
      </div>
      <div className={styles.sphereBottomRight} aria-hidden="true">
        <Image src={imgSphere} alt="" width="100%" height="100%" />
      </div>

      <div className={styles.inner}>
        {/* Header */}
        <div className={styles.headerBlock}>
          <div className={styles.trustRow}>
            <p className={styles.trustLine}>
              {t("blog.carousel.trustedby")} <span aria-hidden="true">❤️</span>
            </p>
            <CustomLink href={viewMoreUrl ?? derivedViewMoreUrl} className={styles.viewMoreLink}>
              {t("blog.carousel.viewmore")}
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path
                  d="M3 8h10M9 4l4 4-4 4"
                  stroke="#ED3A56"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </CustomLink>
          </div>
          <h3 className={styles.title}>{displayTitle}</h3>
        </div>

        {/* Animated card track */}
        <div className={styles.trackArea}>
          <div className={styles.trackViewport}>
            <div
              ref={trackRef}
              className={styles.track}
              style={{
                transform: `translateX(${trackOffset}%)`,
                transition: `transform ${ANIMATION_MS}ms cubic-bezier(0.4,0,0.2,1)`,
              }}
            >
              {extendedItems.map((item, vi) => {
                const diff = vi - virtualIndex;
                const isActive = diff === 0;
                const scale = isActive ? ACTIVE_SCALE : PEEK_SCALE;
                const opacity = Math.abs(diff) > 1 ? 0 : 1;
                const targetVirtual = vi;

                return (
                  <div
                    key={item._virtualKey}
                    className={styles.trackCard}
                    style={{
                      width: `${CARD_WIDTH_PCT}%`,
                      transform: `scale(${scale})`,
                      opacity,
                      transition: `transform ${ANIMATION_MS}ms cubic-bezier(0.4,0,0.2,1), opacity ${ANIMATION_MS}ms ease`,
                      transformOrigin: "center center",
                      cursor: isActive ? "default" : "pointer",
                    }}
                    onClick={!isActive ? () => goTo(targetVirtual) : undefined}
                    role={!isActive ? "button" : undefined}
                    aria-label={!isActive ? `Go to ${item.name}` : undefined}
                    tabIndex={!isActive ? 0 : undefined}
                    onKeyDown={
                      !isActive
                        ? (e) => {
                            if (e.key === "Enter") goTo(targetVirtual);
                          }
                        : undefined
                    }
                  >
                    <InventoryCarouselCard item={item} />
                  </div>
                );
              })}
            </div>
          </div>

          {/* Nav arrows */}
          {total > 1 && (
            <>
              <button
                type="button"
                aria-label="Previous property"
                className={`${styles.navArrow} ${styles.navArrowLeft}`}
                onClick={() => goTo(virtualIndex - 1)}
              >
                <ArrowIcon rotate={90} stroke="#374151" width="16" height="16" strokeWidth="2" />
              </button>
              <button
                type="button"
                aria-label="Next property"
                className={`${styles.navArrow} ${styles.navArrowRight}`}
                onClick={() => goTo(virtualIndex + 1)}
              >
                <ArrowIcon rotate={270} stroke="#374151" width="16" height="16" strokeWidth="2" />
              </button>
            </>
          )}
        </div>

        {/* Dot indicators */}
        <div className={styles.dots} role="tablist" aria-label="Inventory slides">
          {inventories.map((item, i) => {
            const isActiveDot = realActive === i;
            // Jump to the copy2 position for this dot
            const targetVirtual = total + i;
            return (
              <button
                key={item.id}
                type="button"
                role="tab"
                aria-selected={isActiveDot}
                aria-label={`Slide ${i + 1}`}
                className={`${styles.dot} ${isActiveDot ? styles.dotActive : ""}`}
                onClick={() => goTo(targetVirtual)}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default wrapperHOC(memo(ContentInventoryCarousel), {
  componentName: "ContentInventoryCarousel-Blog",
  showForChina: true,
});
